import { createSlice } from "@reduxjs/toolkit"

import { Quotation } from "@/core/model/Quotation"

import { ShippingAddressTo } from "../../../model/Address"
import {
  IAlignerOrder,
  IAlignerOrderPayload,
  PACKAGETYPE,
  RetainerFasttrackOrderType,
  SHIPPINGMETHOD,
} from "../../../model/interface/AlignerOrder"
import { IOrder } from "../../../model/interface/IOrder"
import {
  IAlacarteInfo,
  IBundleInfo,
  IQuotation,
} from "../../../model/interface/IQuotation"
import { IShippingAddressTo } from "../../../model/interface/IShippingAddress"
import {
  createAlternateAddress,
  downloadUassistSummaryPdf,
  getActiveOrdersAPI,
  getCustomPackagePrice,
  getMaterialsAPI,
  getOrdersByCaseId,
  getOrderTypeFilterList,
  getPackageDetail,
  getQuotation,
  getShippingAddressByCase,
  getShippingMethods,
  getStageDetailApi,
  getUAssistOrders,
  placeAlignerOrders,
  placeManufacturingOrders,
  submitRetainerFasttrackOrder,
  updateOrder,
} from "../order/orderThunkApi"

import { IUAssistOrder } from "@/core/model/Order"
import { BundleType } from "@/core/model/BundleType"

interface IData {
  [key: string]: IOrder[]
}

interface IPage {
  currentPage: number
  pageSize: number
  totalRecords: number
  count: number
}

interface OrderTypeFilter{
 label?: string
 bundle_name?: string 
 id?: string 
}

interface OrderState {
  loading: "idle" | "pending" | "succeeded" | "failed"
  orderList: IData
  currentOrder: IAlignerOrder
  paging: IPage
  bundleList: IBundleInfo[]
  bundlesAvailable: boolean
  quotationResponse: object
  bundlesPurchasedForCase: boolean
  previousUassistCredit: number
  previouslyOrderedPricing: string
  hasActiveBundle: boolean
  shippingMethod: SHIPPINGMETHOD
  packageType: PACKAGETYPE | ""
  selectedBundle: IBundleInfo
  currentBundle: string
  alacarte: IAlacarteInfo
  shippingToAddress: IShippingAddressTo
  currency: string
  alignerOrderPayload: IAlignerOrderPayload
  stageDetail: Object
  remainingOrder: { aligner: any; retainer: any; template: any }
  quotation: IQuotation
  isAlignerCase: 0 | 1
  alternateAddress: IShippingAddressTo
  selectedShippingToAddressId: number | undefined
  isCustomPackagingDisabled: boolean
  isRevaPermisson: boolean
  orderData: any
  customPackaging: { setupFee: number; unitCharge: number }
  showAddPackagingPrompt: boolean
  hasCompletedCardOnboarding: boolean
  shippingMethods: any
  uassistSummaryFile:{
    status: "idle" | 'loading' | 'success' | 'error',
    message: any
  }
  uAssistOrders: Array<IUAssistOrder> | null
  orderSource: string | null
  navigateToPatients: boolean
  isBackstopTncAccepted: boolean
  orderTypeFilterList: OrderTypeFilter[]
  retainerFasttrackOrderType: RetainerFasttrackOrderType
  ordersLoaded: boolean
  quotationTaskId: string | null
  /** Lifecycle of the quotation that owns `bundleList`, drives the bundle skeleton. */
  quotationStatus: "idle" | "pending" | "succeeded" | "failed"
  /** Request id of the quotation currently owning the status, so a superseded one can't clear it. */
  quotationRequestId: string | null
  /** Lifecycle of the async aligner order creation, drives the review page skeletons. */
  orderCreationStatus: "idle" | "pending" | "succeeded" | "failed"
  /** Request id of the creation currently owning `currentOrder`, so a superseded task can't overwrite it. */
  orderCreationRequestId: string | null
}

const initialState: OrderState = {
  loading: "idle",
  orderList: {},
  paging: {} as IPage,
  selectedBundle: null,
  shippingMethod: null,
  packageType: "",
  isAlignerCase: 0,
  quotationResponse: null,
  currentBundle: "",
  currentOrder: {
    bundle: {
      pricing_option: "",
      aligner: {
        remaining: null,
        requested: null,
        included: null,
      },
      retainer: {
        remaining: null,
        requested: null,
        included: null,
      },
      alignercase: {
        cost: null,
        count: null,
      },
      previous_uassist_credit: null,
      shipping_price: {
        type: "",
        item_total_value: "",
      },
      custom_packaging: null,
      tax_amt: null,
      sub_total: null,
    },
    alacarte: {
      pricing_option: "",
      aligner: {
        cost: null,
        count: null,
      },
      retainer: {
        cost: null,
        count: null,
      },
      template: {
        cost: null,
        count: null,
      },
      alignercase: {
        cost: null,
        count: null,
      },
      custom_package: {
        cost: null,
        count: null,
      },
      uassist_service: {
        cost: null,
        count: null,
      },
      shipping_price: {
        type: "",
        item_total_value: "",
      },
      custom_packaging: null,
      tax_amt: null,
      sub_total: null,
    },
    total: null,
    shipAddress: undefined,
    currency: "",
    orderId: null,
    subOrderId: null,
  },
  bundleList: [],
  bundlesAvailable: false,
  bundlesPurchasedForCase: false,
  previousUassistCredit: 0,
  previouslyOrderedPricing: "",
  alacarte: {},
  shippingToAddress: {} as IShippingAddressTo,
  currency: "",
  alignerOrderPayload: {
    case_id: null,
    shipping_method: "Standard",
    order_type: "",
    upper_stage_range: "",
    lower_stage_range: "",
    upper_stage_count: null,
    lower_stage_count: null,
    upper_retainer_stage: "",
    lower_retainer_stage: "",
    upper_template_stage: "",
    lower_template_stage: "",
    aligner_material: "",
    thickness: "30",
    package_type: "ulab_packaging",
    upper_left_aligner_coverage: "",
    upper_right_aligner_coverage: "",
    lower_left_aligner_coverage: "",
    lower_right_aligner_coverage: "",
    upper_left_lasttoothtocover: "",
    upper_right_lasttoothtocover: "",
    lower_left_lasttoothtocover: "",
    lower_right_lasttoothtocover: "",
    shipping_address_id: null,
    extra_info: "",
    pricing_option: "",
    quote: undefined,
    complete_upper_stage: "",
    complete_lower_stage: "",
    complete_upper_retainer: "",
    complete_lower_retainer: "",
    complete_upper_template: "",
    complete_lower_template: "",
    pricing_id: "",
    aligner_case: null,
    stage: {
      aligners: { up: [], low: [] },
      retainers: { up: 0, low: 0 },
      templates: { up: null, low: null },
    },
    scanned: [],
    recession: [],
    coverage: {},
    extra: {},
    materials: { aligner: [], retainer: [] },
  },
  selectedShippingToAddressId: undefined,
  stageDetail: {},
  remainingOrder: { aligner: null, retainer: null, template: null },
  quotation: {} as IQuotation,
  alternateAddress: {} as IShippingAddressTo,
  isCustomPackagingDisabled: true,
  isRevaPermisson: false,
  orderData: [],
  customPackaging: { setupFee: null, unitCharge: null },
  showAddPackagingPrompt: false,
  hasCompletedCardOnboarding: false,
  shippingMethods: [],
  uassistSummaryFile:{
    status: "idle",
    message: null
  },
  uassistOrders: null,
  orderSource:null,
  navigateToPatients: false,
  isBackstopTncAccepted: false,
  orderTypeFilterList: [],
  retainerFasttrackOrderType: null,
  ordersLoaded: false,
  quotationTaskId: null,
  quotationStatus: "idle",
  quotationRequestId: null,
  orderCreationStatus: "idle",
  orderCreationRequestId: null,
}

export const OrderServiceSlice = createSlice({
  name: "orderService",
  initialState,
  reducers: {
    resetOrder: (state) => ({
      ...initialState,
      // Preserve the quotation fetched/in-flight from the clinical view so the
      // Bundle page reuses it instead of triggering a duplicate async call.
      quotationTaskId: state.quotationTaskId,
      quotationStatus: state.quotationStatus,
      quotationRequestId: state.quotationRequestId,
      quotationResponse: state.quotationResponse,
      bundleList: state.bundleList,
      alacarte: state.alacarte,
      currency: state.currency,
      remainingOrder: state.remainingOrder,
      quotation: state.quotation,
      bundlesAvailable: state.bundlesAvailable,
      bundlesPurchasedForCase: state.bundlesPurchasedForCase,
      previousUassistCredit: state.previousUassistCredit,
      previouslyOrderedPricing: state.previouslyOrderedPricing,
      hasActiveBundle: state.hasActiveBundle,
      currentBundle: state.currentBundle,
      selectedBundle: state.selectedBundle,
    }),
    setQuotationTaskId: (state, action: { payload: string | null }) => {
      state.quotationTaskId = action.payload
    },
    resetOrderCreationStatus: (state) => {
      state.orderCreationStatus = "idle"
    },
    /**
     * Drops the previously created order so the review page skeletons the whole
     * page instead of briefly showing stale prices from an earlier attempt.
     */
    resetCurrentOrder: (state) => {
      state.currentOrder = initialState.currentOrder
    },
    updateSelectedBundle: (state, action) => {
      state.selectedBundle = action.payload
    },
    updateSelectedShippingMethode: (state, action) => {
      state.shippingMethod = action.payload
    },
    updateSelectedPackageType: (state, action) => {
      state.packageType = action.payload
    },
    updateIsAlignerCase: (state, action) => {
      state.isAlignerCase = action.payload
    },
    updateSelectedShippingToAddressId: (state, action) => {
      state.selectedShippingToAddressId = action.payload
    },
    setShowAddPackagingPrompt: (state, action) => {
      state.showAddPackagingPrompt = action.payload
    },
    setHasCompletedCardOnboarding: (state, action) => {
      state.hasCompletedCardOnboarding = action.payload
    },
    updateAlignerOrderPayload: (state, action) => {
      state.alignerOrderPayload = {
        ...state.alignerOrderPayload,
        ...action.payload,
      }
    },
    updateAlternateAddress: (state, action) => {
      state.alternateAddress = { ...action.payload }
    },
    updateCustomization: (state, action) => {
      state.alignerOrderPayload = {
        ...state.alignerOrderPayload,
        ...action.payload,
      }
    },
    updateOrderStage: (state, action) => {
      if (action?.payload?.aligners?.low) {
        action.payload.aligners.low.sort()
      }
      if (action?.payload?.aligners?.up) {
        action.payload.aligners.up.sort()
      }
      state.alignerOrderPayload.stage = {
        ...action.payload,
      }
    },
    resetPayload: (state) => {
      state.alignerOrderPayload = {} as IAlignerOrderPayload
    },
    setOrderSource:(state,action)=>{
      state.orderSource = action.payload
    },
    updateNavigateToPatients:(state, action)=>{
      state.navigateToPatients = action.payload
    },
    updateIsBackstopTncAccepted:(state, action)=>{
      state.isBackstopTncAccepted = action.payload
    },
    updateRetainerFasttrackOrderType:(state, action)=>{
      state.retainerFasttrackOrderType = action.payload
    },
    setOrdersLoaded: (state, action) => {
      state.ordersLoaded = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateOrder.pending, (state) => {
      state.loading = "pending"
    })
    builder.addCase(updateOrder.fulfilled, (state) => {
      state.loading = "succeeded"
    })
    builder.addCase(updateOrder.rejected, (state) => {
      state.loading = "failed"
    })
    builder.addCase(getOrdersByCaseId.pending, (state) => {
      state.loading = "pending"
    })
    builder.addCase(getOrdersByCaseId.fulfilled, (state, action) => {
      state.orderList = {
        ...state.orderList,
        [action.meta.arg]: action.payload.length ? action.payload : null,
      }
      state.loading = "succeeded"
    })
    builder.addCase(getOrdersByCaseId.rejected, (state) => {
      state.loading = "failed"
    })
    builder.addCase(getQuotation.pending, (state, action) => {
      if (!action.meta.arg?.isQuoteUpdate && !action.meta.arg?.silent) {
        state.loading = "pending"
      }
      // A quote update refreshes prices around the list the user is already
      // looking at; anything else replaces the list, so it must be awaited.
      if (!action.meta.arg?.isQuoteUpdate) {
        state.quotationStatus = "pending"
        state.quotationRequestId = action.meta.requestId
      }
    })

    builder.addCase(getQuotation.fulfilled, (state, action) => {
      const isQuoteUpdate = action.meta?.arg?.isQuoteUpdate
      const silent = action.meta?.arg?.silent
      if (!isQuoteUpdate && !silent) {
        state.loading = "succeeded"
      }
      if (action.meta.requestId === state.quotationRequestId) {
        state.quotationStatus = "succeeded"
      }
      state.quotationResponse = action.payload
      const res: IQuotation = new Quotation().Deserialize(action.payload)
      state.bundleList = res.bundles
      state.alacarte = res.alacarte
      state.currency = res.currency
      state.remainingOrder = res.remainingOrder
      state.quotation = res
      state.quotation.isAlignerCase = res.isAlignerCase
      state.bundlesAvailable = res.bundlesAvailable
      state.bundlesPurchasedForCase = res.bundlesPurchasedForCase
      state.previousUassistCredit = res.previousUassistCredit
      state.previouslyOrderedPricing = res.previouslyOrderedPricing
      state.hasActiveBundle = res.hasActiveBundle
      if (isQuoteUpdate) return
      if (
        res.previouslyOrderedPricing &&
        res.bundlesAvailable &&
        res.bundles?.length
      ) {
        state.currentBundle = res.bundles?.find(
          (a) => a.name?.toLowerCase() === res.previouslyOrderedPricing?.toLowerCase()
        )?.type
        state.selectedBundle = res.bundles?.find(
          (a) => a.name?.toLowerCase() === res.previouslyOrderedPricing?.toLowerCase()
        )
      }
      if (
        res.previouslyOrderedPricing &&
        res.previouslyOrderedPricing.toLowerCase() === BundleType.ALACARTE
      ) {
        state.selectedBundle = res.alacarte
        state.currentBundle = res.alacarte.type
      }
      state.quotationTaskId = null
    })
    builder.addCase(getQuotation.rejected, (state, action) => {
      if (!action.meta.arg?.isQuoteUpdate && !action.meta.arg?.silent) {
        state.loading = "failed"
      }
      if (action.meta.requestId === state.quotationRequestId) {
        state.quotationStatus = "failed"
      }
      state.quotationTaskId = null
    })
    builder.addCase(placeAlignerOrders.pending, (state, action) => {
      state.loading = "pending"
      state.orderCreationStatus = "pending"
      state.orderCreationRequestId = action.meta.requestId
    })
    builder.addCase(placeAlignerOrders.fulfilled, (state, action) => {
      // A creation left behind by a previous attempt must never overwrite the
      // order the user is currently reviewing.
      if (action.meta.requestId !== state.orderCreationRequestId) return
      state.orderCreationStatus = "succeeded"
      state.currentOrder = action.payload
      state.shippingToAddress = new ShippingAddressTo().Deserialize(
        action.payload,
      )
      if(action.payload.shippingMethod){
        state.shippingMethod = action.payload.shippingMethod
      }
      state.packageType = action.payload.packageType

      state.loading = "succeeded"
    })
    builder.addCase(placeAlignerOrders.rejected, (state, action) => {
      if (action.meta.requestId !== state.orderCreationRequestId) return
      state.loading = "failed"
      state.orderCreationStatus = "failed"
    })
    builder.addCase(getShippingAddressByCase.pending, (state) => {
      state.loading = "pending"
    })
    builder.addCase(getShippingAddressByCase.fulfilled, (state, action) => {
      state.loading = "succeeded"
      state.shippingToAddress = action.payload
    })
    builder.addCase(getShippingAddressByCase.rejected, (state) => {
      state.loading = "failed"
    })

    builder.addCase(placeManufacturingOrders.pending, (state) => {
      state.loading = "pending"
    })
    builder.addCase(placeManufacturingOrders.fulfilled, (state, action) => {
      state.loading = "succeeded"
    })
    builder.addCase(placeManufacturingOrders.rejected, (state) => {
      state.loading = "failed"
    })
    builder.addCase(submitRetainerFasttrackOrder.pending, (state) => {
      state.loading = "pending"
    })
    builder.addCase(submitRetainerFasttrackOrder.fulfilled, (state, action) => {
      state.loading = "succeeded"
    })
    builder.addCase(submitRetainerFasttrackOrder.rejected, (state) => {
      state.loading = "failed"
    })
    builder.addCase(getPackageDetail.pending, (state) => {
      state.loading = "pending"
    })
    builder.addCase(getPackageDetail.fulfilled, (state, action) => {
      if (action.payload?.result?.custom_logo) {
        state.isCustomPackagingDisabled = false
      }
      if (action.payload?.result?.org_features) {
        action.payload?.result?.org_features.map((i) => {
          if (i.type.name === "Reva") {
            state.isRevaPermisson = true
          }
        })
      }
      state.loading = "succeeded"
    })
    builder.addCase(getPackageDetail.rejected, (state) => {
      state.loading = "failed"
    })

    builder.addCase(getStageDetailApi.fulfilled, (state, action) => {
      state.loading = "succeeded"
      const {
        payload: { results },
      } = action
      state.stageDetail = results
    })
    builder.addCase(getStageDetailApi.rejected, (state) => {
      state.loading = "failed"
    })

    builder.addCase(createAlternateAddress.pending, (state) => {
      state.loading = "pending"
    })
    builder.addCase(createAlternateAddress.fulfilled, (state, action) => {
      state.loading = "succeeded"
    })
    builder.addCase(createAlternateAddress.rejected, (state) => {
      state.loading = "failed"
    })
    builder.addCase(getMaterialsAPI.pending, (state) => {
      state.loading = "pending"
    })
    builder.addCase(getMaterialsAPI.fulfilled, (state, action) => {
      state.loading = "succeeded"
      state.alignerOrderPayload.materials = action.payload
    })
    builder.addCase(getMaterialsAPI.rejected, (state) => {
      state.loading = "failed"
    })
    builder.addCase(getActiveOrdersAPI.pending, (state) => {
      state.loading = "pending"
    })
    builder.addCase(getActiveOrdersAPI.fulfilled, (state, action) => {
      state.loading = "succeeded"
      const { records, totalRecords, currentPage, pageSize } = action.payload
      state.orderData = records || []
      state.paging = {
        currentPage: currentPage,
        pageSize: pageSize,
        totalRecords: totalRecords,
        count: totalRecords,
      }
    })
    builder.addCase(getActiveOrdersAPI.rejected, (state) => {
      state.loading = "failed"
    })
    builder.addCase(getCustomPackagePrice.pending, (state) => {
      state.loading = "pending"
    })
    builder.addCase(getCustomPackagePrice.fulfilled, (state, action) => {
      if (action.payload?.result) {
        const promoEngineResults = action.payload?.result["promo-engine"]
        if (promoEngineResults && promoEngineResults.length > 0) {
          const quote = promoEngineResults[0].quote
          if (quote) {
            state.customPackaging = {
              setupFee: quote["custom-packaging-artwork"].total,
              unitCharge: quote["custom-packaging"].total,
            }
          }
        }
      }
      state.loading = "succeeded"
    })

    builder.addCase(getCustomPackagePrice.rejected, (state) => {
      state.loading = "failed"
    })
    builder.addCase(getShippingMethods.rejected, (state) => {
      state.loading = "failed"
    })
    builder.addCase(getShippingMethods.pending, (state) => {
      state.loading = "pending"
    })
    builder.addCase(getShippingMethods.fulfilled, (state, action) => {
      state.loading = "succeeded"
      state.shippingMethods = action.payload
    })
    builder.addCase(downloadUassistSummaryPdf.pending, (state) => {
      state.uassistSummaryFile.status = "loading"
    })
    builder.addCase(downloadUassistSummaryPdf.fulfilled, (state, action) => {
      state.uassistSummaryFile.status = "success"
      state.uassistSummaryFile.message = action.payload
    })
    builder.addCase(downloadUassistSummaryPdf.rejected, (state, action) => {
      state.uassistSummaryFile.status = "error"
      state.uassistSummaryFile.message = action.payload
    })
    builder.addCase(getUAssistOrders.pending, (state)=>{
      state.loading = "pending"
    })
    builder.addCase(getUAssistOrders.rejected, (state)=>{
      state.loading = "failed"
    })
    builder.addCase(getUAssistOrders.fulfilled, (state, action)=>{
      state.uAssistOrders = action.payload
      state.loading = "succeeded"
    })
    builder.addCase(getOrderTypeFilterList.pending, (state)=>{
      state.loading = "pending"
    })
    builder.addCase(getOrderTypeFilterList.rejected, (state)=>{
      state.loading = "failed"
    })
    builder.addCase(getOrderTypeFilterList.fulfilled, (state, action)=>{
      state.orderTypeFilterList = action.payload
      state.loading = "succeeded"
    })
  },
})

export const {
  updateAlignerOrderPayload,
  updateSelectedBundle,
  updateSelectedShippingMethode,
  updateSelectedPackageType,
  updateIsAlignerCase,
  updateAlternateAddress,
  updateSelectedShippingToAddressId,
  setShowAddPackagingPrompt,
  setHasCompletedCardOnboarding,
  updateCustomization,
  updateOrderStage,
  resetPayload,
  resetOrder,
  setOrderSource,
  updateNavigateToPatients,
  updateIsBackstopTncAccepted,
  updateRetainerFasttrackOrderType,
  setOrdersLoaded,
  setQuotationTaskId,
  resetOrderCreationStatus,
  resetCurrentOrder,
} = OrderServiceSlice.actions
